"use client";
import { getApiBaseUrl } from "@/lib/apiBase";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Grid2x2, List, Calendar } from "lucide-react";

const BASE_URL = getApiBaseUrl();

function shouldUnoptimizeImage(src) {
  return typeof src === "string" && /^data:/i.test(src);
}

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const response = await fetch("/api/blog?type=blog");
        const data = await response.json();
        setBlogs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const categories = useMemo(() => {
    return blogs
      .map((blog) => blog.category)
      .filter(Boolean)
      .reduce((acc, cat) => (acc.includes(cat) ? acc : [...acc, cat]), []);
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    return blogs.filter((blog) => {
      const category = blog.category || "";
      return !selectedCategory || category === selectedCategory;
    });
  }, [blogs, selectedCategory]);

  if (loading) {
    return (
      <div className="bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 space-y-8">
          <div className="h-36 animate-pulse rounded-2xl bg-gray-200" />
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow animate-pulse"
              >
                <div className="h-28 w-28 flex-shrink-0 rounded-xl bg-gray-200" />
                <div className="flex-1 space-y-3 py-1">
                  <div className="h-4 w-24 rounded-full bg-gray-200" />
                  <div className="h-5 w-3/4 rounded-full bg-gray-200" />
                  <div className="h-4 w-full rounded-full bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 space-y-8">
        <header className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#577099]">
                Блог
              </span>
              <h1 className="mt-2 text-3xl font-bold text-gray-900">Сүүлийн нийтлэлүүд</h1>
            </div>
            <div className="flex h-10 items-center gap-2 rounded-2xl border border-gray-200 bg-white px-2 text-xs text-gray-500 shadow-sm">
              <span>Харах горим:</span>
              <ViewToggleButton
                icon={<Grid2x2 className="h-4 w-4" />}
                label="Сүлжээ"
                active={viewMode === "grid"}
                onClick={() => setViewMode("grid")}
              />
              <ViewToggleButton
                icon={<List className="h-4 w-4" />}
                label="Жагсаалт"
                active={viewMode === "list"}
                onClick={() => setViewMode("list")}
              />
            </div>
          </div>

          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <CategoryChip
                label="Бүх ангилал"
                active={!selectedCategory}
                onClick={() => setSelectedCategory("")}
              />
              {categories.map((cat) => (
                <CategoryChip
                  key={cat}
                  label={cat}
                  active={selectedCategory === cat}
                  onClick={() => setSelectedCategory(cat)}
                />
              ))}
            </div>
          )}
        </header>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            Нийт <span className="font-semibold text-gray-800">{filteredBlogs.length}</span> нийтлэл
          </p>
        </div>

        <main className="space-y-8">
          {filteredBlogs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white/80 p-12 text-center shadow-inner">
              <h2 className="text-xl font-semibold text-gray-700 mb-3">
                {blogs.length === 0
                  ? "Одоогоор нийтлэл алга"
                  : "Тухайн ангилалд нийтлэл алга"}
              </h2>
              <p className="text-gray-500">
                {blogs.length === 0
                  ? "Удахгүй шинэ нийтлэлүүд нэмэгдэнэ."
                  : 'Бусад ангилал сонгож эсвэл "Бүх ангилал" горимд буцааж бүх нийтлэлийг хараарай.'}
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid gap-6 md:grid-cols-2">
              {filteredBlogs.map((blog) => (
                <BlogGridCard key={blog.id} blog={blog} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBlogs.map((blog) => (
                <BlogListCard key={blog.id} blog={blog} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function CategoryChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-xs font-semibold transition ${
        active
          ? "bg-[#577099] text-white shadow"
          : "border border-gray-200 bg-white text-gray-600 hover:border-[#577099]/20 hover:text-[#577099]"
      }`}
    >
      {label}
    </button>
  );
}

function ViewToggleButton({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
        active ? "bg-[#577099] text-white shadow-sm" : "text-gray-500 hover:text-[#577099]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function BlogGridCard({ blog }) {
  const imageSrc = blog.image ? `${BASE_URL}${blog.image}` : null;
  const published = blog.publishedAt || "Огноо тодорхойгүй";
  const category = blog.category || "Ангилалгүй";

  return (
    <Link
      href={`/blog/${blog.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-lg hover:shadow-[#577099]/20"
      aria-label={blog.title}
    >
      <div className="relative h-44 w-full bg-gray-100">
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt={blog.title}
            fill
            className="object-cover transition duration-500 group-hover:scale-105"
            unoptimized={shouldUnoptimizeImage(imageSrc)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#577099] to-[#4a5f85] text-3xl text-white/70">
            ✍️
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5 space-y-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#577099]">
          {category}
        </span>
        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-[#577099]">
          {blog.title}
        </h3>
        {blog.excerpt && (
          <p className="text-sm text-gray-600 line-clamp-3">{blog.excerpt}</p>
        )}
        <p className="mt-auto text-xs text-gray-500">{published}</p>
      </div>
    </Link>
  );
}

function BlogListCard({ blog }) {
  const imageSrc = blog.image ? `${BASE_URL}${blog.image}` : null;
  const published = blog.publishedAt || "Огноо тодорхойгүй";
  const category = blog.category || "Ангилалгүй";

  return (
    <Link
      href={`/blog/${blog.id}`}
      className="group flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg hover:shadow-[#577099]/20"
      aria-label={blog.title}
    >
      <div className="relative h-28 w-28 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100">
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt={blog.title}
            fill
            className="object-cover transition duration-500 group-hover:scale-105"
            unoptimized={shouldUnoptimizeImage(imageSrc)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#577099] to-[#4a5f85] text-2xl text-white/70">
            ✍️
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col space-y-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#577099]">
          {category}
        </span>
        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-[#577099]">
          {blog.title}
        </h3>
        {blog.excerpt && (
          <p className="text-sm text-gray-600 line-clamp-2">{blog.excerpt}</p>
        )}
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5 text-gray-400" />
          {published}
        </p>
      </div>
    </Link>
  );
}
